/**
 * @file Components/Ui/ImageUploader.jsx
 * @description Reusable image upload component with two modes:
 * 1. Paste URL — text input for image URL
 * 2. Upload File — uploads to Cloudinary via /api/settings/upload
 * Used across admin panel (SiteContent, Tours, Blogs, etc.)
 */

import React, { useState, useRef } from 'react';
import { Loader2, X, Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadSiteImage } from '../../services/api';

const ImageUploader = ({ label, value, onChange, hint, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('url');
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadSiteImage(file);
      const url = res?.data?.data?.url || res?.data?.url;
      if (url) {
        onChange(url);
        toast.success('Image uploaded to Cloudinary!');
      } else {
        toast.error('Upload succeeded but no URL returned');
      }
    } catch (err) {
      toast.error('Upload failed: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <div className="flex gap-1">
            <button type="button" onClick={() => setMode('url')}
              className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${mode === 'url' ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}>
              <LinkIcon size={10} /> URL
            </button>
            <button type="button" onClick={() => setMode('upload')}
              className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${mode === 'upload' ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}>
              <Upload size={10} /> Upload
            </button>
          </div>
        </div>
      )}

      {mode === 'url' ? (
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or /path/to/image"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500" />
      ) : (
        <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          uploading ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
        }`}>
          {uploading ? (
            <><Loader2 size={16} className="animate-spin text-indigo-500" /> <span className="text-sm text-indigo-600">Uploading to Cloudinary...</span></>
          ) : (
            <><Upload size={16} className="text-gray-400" /> <span className="text-sm text-gray-500">Click to choose image (max 5MB)</span></>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} className="hidden" />
        </label>
      )}

      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}

      {value && (
        <div className="mt-2">
          <div className="relative group inline-block">
            <img src={value} alt="Preview" className="h-24 w-auto max-w-full object-cover rounded-lg border"
              onError={(e) => { e.target.style.display = 'none'; }} />
            <button type="button" onClick={() => onChange('')}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow"
              title="Remove image">
              <X size={12} />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1 truncate max-w-md" title={value}>{value}</p>
        </div>
      )}
    </div>
  );
};

/**
 * Multi-image uploader for galleries. Each image can be URL or uploaded.
 */
export const MultiImageUploader = ({ label, images, onChange, hint }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFilesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Please select image files');
      return;
    }
    setUploading(true);
    const newUrls = [];
    for (const file of imageFiles) {
      try {
        const res = await uploadSiteImage(file);
        const url = res?.data?.data?.url || res?.data?.url;
        if (url) newUrls.push(url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      onChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} image(s) uploaded!`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const addUrlSlot = () => onChange([...images, '']);
  const updateUrl = (i, val) => { const copy = [...images]; copy[i] = val; onChange(copy); };
  const removeImage = (i) => onChange(images.filter((_, j) => j !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <div className="flex gap-2">
          <button type="button" onClick={addUrlSlot} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <LinkIcon size={10} /> Add URL
          </button>
          <label className={`text-xs flex items-center gap-1 cursor-pointer ${uploading ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-800'}`}>
            <Upload size={10} /> Upload Files
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFilesUpload} disabled={uploading} className="hidden" />
          </label>
        </div>
      </div>
      {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-indigo-600 mb-2">
          <Loader2 size={14} className="animate-spin" /> Uploading to Cloudinary...
        </div>
      )}

      {/* Image grid */}
      <div className="grid grid-cols-4 gap-3">
        {images.map((img, i) => (
          <div key={i} className="relative group">
            {img ? (
              <>
                <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-24 object-cover rounded-lg border"
                  onError={(e) => { e.target.src = ''; e.target.style.display = 'none'; }} />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow">
                  <X size={12} />
                </button>
              </>
            ) : (
              <div className="relative">
                <input type="text" placeholder="Paste URL..." value={img}
                  onChange={(e) => updateUrl(i, e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none" />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow">
                  <X size={10} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {images.length === 0 && !uploading && (
        <p className="text-xs text-gray-400 text-center py-4 border-2 border-dashed rounded-lg">
          No gallery images yet. Click "Upload Files" or "Add URL" above.
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
